import root from 'window-or-global';
import {isMobilePhone} from 'validator';
import {jobFetcher} from '../jobs.fetch';
import {FORM_ERROR} from 'final-form';

export const mapFieldType = (fieldType) => {
    switch (fieldType) {
        case 'multi_value_single_select':
            return 'select';
        case 'multi_value_multi_select':
            return 'select';
        case 'input_text':
            return 'input';
        case 'textarea':
            return 'textarea';
        default:
    }
};

export const isMultiSelect = (fieldType) => {
    switch (fieldType) {
        case 'multi_value_multi_select':
            return true;
        default:
            return false;
    }
};

export const demographicQuestionsFormatted = (demographicQuestions) => {
    //Format demographic questions into array of objects
    const demographicQuestionsArray = Object.entries(demographicQuestions);
    const demographicQuestionsFormatted = demographicQuestionsArray.map((ar) => {
        const ar1 = ar[0].replace('optionID', '');
        const ar2: any = ar[1];

        return {
            question_id: Number(ar1),
            answer_options: [
                {
                    answer_option_id: Number(ar2.replace('answerID', ''))
                }
            ]
        };
    });
    return demographicQuestionsFormatted;
};

export const covertFileToBase64 = (file) => {
    return new Promise((resolve) => {
        // Encode the file using the FileReader API
        const reader = new FileReader();

        reader.onloadend = () => {
            // Use a regex to remove data url part
            const base64String = reader?.result;
            resolve(base64String?.toString()?.replace('data:', '')?.replace(/^.+,/, ''));
        };

        reader.readAsDataURL(file);
    });
};

export const formatFilesJSON = async (files) => {
    const filesOBJ = {};
    for (const [key] of Object.entries(files)) {
        filesOBJ[`${key}_content`] = await covertFileToBase64(files[key][0]);
        filesOBJ[`${key}_content_filename`] = files[key][0].name;
    }
    return filesOBJ;
};

export const formatSubmitJSON = async (values) => {
    const formSubmitValuesJSON = {...values.questions};

    if (values.mapped_url_token) {
        formSubmitValuesJSON.mapped_url_token = values.mapped_url_token;
    }

    if (values?.educations?.length > 0) {
        const formattedEducationsArray = values?.educations?.filter((item) => item !== null) || []; // remove nulls caused by removed blocks
        formSubmitValuesJSON.educations = [...formattedEducationsArray];
    }

    const filesJSON = values?.file ? await formatFilesJSON(values?.file) : {};

    if (values?.demographicQuestions) {
        formSubmitValuesJSON.demographic_answers = demographicQuestionsFormatted(
            values.demographicQuestions
        );
    }

    return new Promise((resolve) => {
        resolve({...formSubmitValuesJSON, ...filesJSON});
    });
};

export const validEmail = (email) => {
    return String(email)
        .toLowerCase()
        .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );
};

export const validPhone = (phone) => {
    if (phone && typeof phone === 'string') {
        return isMobilePhone(phone.replace(/[\s.]+/g, ''));
    }
    return true;
};

export const validResumeOrCoverLetter = (fileInput, textarea) => {
    return fileInput !== undefined || !!textarea;
};

export const fileNotEmpty = (value: unknown) => {
    if (value && value[0]) {
        const file_size = value[0].size;
        if (file_size && file_size < 1) {
            return 'epic.jobs.form.validation.fileCannotBeBlank';
        }
    }
    return undefined;
};

export const validFileExt = (value) => {
    if (value && value[0]) {
        const filename = value[0].name;
        if (filename && filename.match(/^.*\.(pdf|doc|docx|txt|rtf)$/i)) {
            return undefined;
        }
        return 'epic.jobs.form.validation.invalidFileExtension';
    }
    return undefined;
};

export const validWebsite = (value) => {
    const websiteRegex =
        /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()!@:%_\+.~#?&\/\/=]*)$/; // eslint-disable-line no-useless-escape

    if (String(value).toLowerCase().match(websiteRegex)) {
        return undefined;
    } else {
        return 'epic.jobs.form.validation.invalidWebsiteUrl';
    }
};

export const validYear = (value) => {
    const currentYear = new Date().getFullYear();
    const yearMinimum = currentYear - 90;
    const yearMax = currentYear + 50;
    const validYear = value >= yearMinimum && value <= yearMax;

    if (value === undefined || value === null || validYear) {
        return undefined;
    } else {
        return 'epic.jobs.form.validation.invalidYear';
    }
};

export const validMonth = (value) => {
    const validMonth = value >= 1 && value <= 12;

    if (value === undefined || value === null || validMonth) {
        return undefined;
    } else {
        return 'epic.jobs.form.validation.invalidMonth';
    }
};

export const required = (value) =>
    value?.trim() ? undefined : 'epic.jobs.form.validation.required';

/**
 * Class used to tie a question's file input, textarea and 'Manually Enter' button together.
 * The question label alone is not safe: labels can be entirely non-latin (Chinese, Japanese...),
 * and stripping non word characters then leaves an empty string, which turns the lookups below
 * into the invalid selector `.resumeOrCoverLetter-text.`. Prefixing with the question index keeps
 * the class a valid selector and unique per question.
 */
export const getQuestionClassName = (label: string | undefined, index: number): string => {
    const alphaNumeric = label?.replace(/\W/g, '');
    return alphaNumeric ? `question-${index}-${alphaNumeric}` : `question-${index}`;
};

const getQuestionElement = (selector: string, objClassName?: string): Element | null => {
    if (!objClassName) return null;
    return root.document.querySelector(`${selector}.${objClassName}`);
};

export const toggleFileOption = (objClassName) => {
    //show button 'Manually Enter'
    const buttonManuallyEnter = getQuestionElement(
        '.resumeOrCoverLetter-manuallyEnter',
        objClassName
    );
    if (buttonManuallyEnter) {
        buttonManuallyEnter.classList.remove('hidden');
    }

    //hide textarea
    const textArea = getQuestionElement('.resumeOrCoverLetter-text', objClassName);
    if (textArea) {
        textArea.classList.add('hidden');
    }

    return false;
};
export const toggleFilePlaintextOption = (objClassName) => {
    //show text area to Manually Enter content
    const fileTextArea = getQuestionElement('.resumeOrCoverLetter-text', objClassName);
    fileTextArea?.classList.remove('hidden');

    //hide button 'Manually Enter'
    const buttonManuallyEnter = getQuestionElement(
        '.resumeOrCoverLetter-manuallyEnter',
        objClassName
    );
    buttonManuallyEnter?.classList.add('hidden');

    return false;
};

export const isFilePlaintextOptionOpen = (objClassName) => {
    const fileTextArea = getQuestionElement('.resumeOrCoverLetter-text', objClassName);
    return !(fileTextArea && fileTextArea.classList.contains('hidden'));
};

export const globalFormValidation = (values) => {
    const errors: any = {questions: {required: {}}, file: {required: {}}};
    if (!validEmail(values?.questions?.email)) {
        errors.questions.email = 'epic.jobs.form.validation.invalidEmail';
    }
    if (!validPhone(values?.questions?.phone)) {
        errors.questions.phone = 'epic.jobs.form.validation.invalidPhone';
    }
    if (
        values?.resumeRequired &&
        !validResumeOrCoverLetter(values?.file?.resume, values.questions?.resume_text)
    ) {
        errors.file.resume = 'epic.jobs.form.validation.resumeRequired';
        errors.questions.resume_text = 'epic.jobs.form.validation.resumeRequired';
    }
    if (
        values?.coverletterRequired &&
        !validResumeOrCoverLetter(values?.file?.cover_letter, values.questions?.cover_letter_text)
    ) {
        errors.file.cover_letter = 'epic.jobs.form.validation.coverLetterRequired';
        errors.questions.cover_letter_text = 'epic.jobs.form.validation.coverLetterRequired';
    }
    if (values.educations && values.educations.length > 0) {
        errors.educations = [];
        const dateError = 'epic.jobs.form.validation.invalidDateRange';
        values.educations.forEach((value, key) => {
            errors.educations[key] = {
                start_date: {},
                end_date: {}
            };
            if (value.start_date || value.end_date) {
                if (!value?.start_date?.year) {
                    errors.educations[key].start_date.year = validYear(0);
                }
                if (!value?.start_date?.month) {
                    errors.educations[key].start_date.month = validMonth(0);
                }
                if (!value?.end_date?.year) {
                    errors.educations[key].end_date.year = validYear(0);
                }
                if (!value?.end_date?.month) {
                    errors.educations[key].end_date.month = validMonth(0);
                }
            }
            if (
                value.start_date &&
                value.start_date.year &&
                value.start_date.month &&
                value.end_date &&
                value.end_date.year &&
                value.end_date.month
            ) {
                const startDate = new Date(`01-${value.start_date.month}-${value.start_date.year}`);
                const endDate = new Date(`01-${value.end_date.month}-${value.end_date.year}`);
                if (startDate > endDate) {
                    errors.educations[key].start_date.year = dateError;
                    errors.educations[key].start_date.month = dateError;
                    errors.educations[key].end_date.year = dateError;
                    errors.educations[key].end_date.month = dateError;
                }
            }
        });
    }
    return errors;
};

export const scrollToSubmitMessage = () => {
    const el = root.document.getElementsByClassName('submitSuccessMsg')[0];
    if (el) el.scrollIntoView();
    //scroll extra 200px up
    root.scrollTo(0, window.pageYOffset - 200);
};

export const submitJobApplication = async (
    jobId,
    values,
    setSubmitMessage,
    setShowJobApplicationForm,
    setSubmitErrorMsg
) => {
    setSubmitErrorMsg('');
    const formattedSubmitValues = await formatSubmitJSON(values);
    let submitJobResponse;
    try {
        submitJobResponse = await jobFetcher.submitJobApplication(
            jobId,
            formattedSubmitValues || {}
        );
    } catch (e) {
        submitJobResponse = null;
        setSubmitErrorMsg('epic.jobs.form.validation.submitError');
        return {[FORM_ERROR]: 'epic.jobs.form.validation.submitError'};
    }

    if (submitJobResponse?.success) {
        setSubmitMessage(submitJobResponse.success);
        setShowJobApplicationForm(false);
        scrollToSubmitMessage();
    } else {
        setSubmitErrorMsg(
            `epic.jobs.form.validation.submitError${
                submitJobResponse?.error ? `: ${submitJobResponse.error}` : ''
            }`
        );
        return {[FORM_ERROR]: 'epic.jobs.form.validation.submitError'};
    }
};

export const handleReset = (form, setSubmitErrorMsg) => {
    setSubmitErrorMsg('');
    const {resumeRequired} = form.getState().values; // replace 'myField' with the name of the field you want to keep
    form.restart();
    form.change('resumeRequired', resumeRequired);
};

export const translateValidationError = (error: any, getMessage: (key: string) => string): any => {
    if (!error) return error;

    if (typeof error === 'string' && error.startsWith('epic.jobs.form.validation.')) {
        return getMessage(error);
    }

    if (typeof error === 'object' && error !== null) {
        const translatedError: any = Array.isArray(error) ? [] : {};

        for (const key in error) {
            if (Object.prototype.hasOwnProperty.call(error, key)) {
                translatedError[key] = translateValidationError(error[key], getMessage);
            }
        }

        return translatedError;
    }

    return error;
};

export const createLocalizedValidator = (
    validator: (...args: any[]) => any,
    getMessage: (key: string) => string
) => {
    return (...args: any[]) => {
        const result = validator(...args);
        return translateValidationError(result, getMessage);
    };
};
